import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Sparkles, Send, MessageCircle, Mail, Trash2, ArrowLeft, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", channel: "both", subject: "", body: "", recipient_ids: [] });
  const [contacts, setContacts] = useState([]);
  const [data, setData] = useState(null);
  const [dispatch, setDispatch] = useState(null);
  const [ai, setAi] = useState({ goal: "", offer: "", tone: "profissional", channel: "whatsapp" });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [sentSet, setSentSet] = useState(new Set());

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data));
    api.get(`/campaigns/${id}`).then((r) => { setForm({ ...r.data }); setData(r.data); });
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const r = await api.put(`/campaigns/${id}`, form);
    setData(r.data);
    toast.success("Salvo");
  };

  const remove = async () => {
    await api.delete(`/campaigns/${id}`);
    toast.success("Excluída");
    navigate("/prospecting");
  };

  const toggleRecipient = (cid) => {
    setForm((f) => ({ ...f, recipient_ids: f.recipient_ids.includes(cid) ? f.recipient_ids.filter((x) => x !== cid) : [...f.recipient_ids, cid] }));
  };
  const selectAll = () => setForm((f) => ({ ...f, recipient_ids: contacts.map((c) => c.contact_id) }));
  const clearAll = () => setForm((f) => ({ ...f, recipient_ids: [] }));

  const generateAI = async () => {
    if (!ai.goal) return toast.error("Descreva o objetivo");
    setAiLoading(true);
    try {
      const r = await api.post("/campaigns/ai-generate", ai);
      setForm((f) => ({ ...f, subject: r.data.subject || f.subject, body: r.data.body || f.body }));
      toast.success("Mensagem gerada pela IA");
      setAiOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na IA");
    } finally { setAiLoading(false); }
  };

  const loadDispatch = async () => {
    await save();
    const r = await api.get(`/campaigns/${id}/dispatch`);
    setDispatch(r.data);
  };

  const markSent = async () => {
    await api.post(`/campaigns/${id}/mark-sent`, { count: sentSet.size });
    toast.success("Campanha marcada como enviada");
    const r = await api.get(`/campaigns/${id}`);
    setData(r.data);
    setForm({ ...r.data });
  };

  const openOne = (item, channel) => {
    const url = channel === "whatsapp" ? item.whatsapp_url : item.mailto_url;
    if (!url) return toast.error(`Contato sem ${channel === "whatsapp" ? "telefone" : "e-mail"}`);
    window.open(url, "_blank");
    setSentSet((s) => new Set(s).add(`${item.contact_id}:${channel}`));
  };

  const isSent = (item, channel) => sentSet.has(`${item.contact_id}:${channel}`);

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <button onClick={() => navigate("/prospecting")} className="text-xs text-muted-foreground hover:text-[#C07C88] mb-2 inline-flex items-center gap-1"><ArrowLeft size={12} /> Voltar</button>
          <p className="overline">{data?.campaign_id?.slice(0, 12) || "Campanha"}</p>
          <h1 className="font-heading text-4xl mt-2">{form.name || "Editar Campanha"}</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#D4AF37] hover:bg-[#B8961F] text-white" data-testid="ai-campaign-btn"><Sparkles size={14} className="mr-1" /> Gerar com IA</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-2xl flex items-center gap-2"><Sparkles size={18} className="text-[#D4AF37]" /> Copy IA</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Objetivo</Label><Input placeholder="Ex: agendar reunião de descoberta" value={ai.goal} onChange={(e) => setAi({ ...ai, goal: e.target.value })} data-testid="ai-goal" /></div>
                <div><Label>Oferta / Pitch</Label><Input placeholder="Ex: treinamento de liderança para gerentes médios" value={ai.offer} onChange={(e) => setAi({ ...ai, offer: e.target.value })} data-testid="ai-offer" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tom</Label>
                    <Select value={ai.tone} onValueChange={(v) => setAi({ ...ai, tone: v })}>
                      <SelectTrigger data-testid="ai-tone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="profissional">Profissional</SelectItem>
                        <SelectItem value="consultivo">Consultivo</SelectItem>
                        <SelectItem value="direto">Direto ao ponto</SelectItem>
                        <SelectItem value="acolhedor">Acolhedor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Canal</Label>
                    <Select value={ai.channel} onValueChange={(v) => setAi({ ...ai, channel: v })}>
                      <SelectTrigger data-testid="ai-channel"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="email">E-mail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={generateAI} disabled={aiLoading} data-testid="ai-run-btn">{aiLoading ? "Gerando…" : "Gerar mensagem"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={14} className="mr-1" /> Excluir</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Excluir campanha?</AlertDialogTitle><AlertDialogDescription>Ação permanente.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={remove}>Excluir</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={save} data-testid="save-campaign-btn">Salvar</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Mensagem</p>
            <div className="space-y-4">
              <div><Label>Nome da campanha</Label><Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="campaign-name" /></div>
              <div>
                <Label>Canal</Label>
                <Select value={form.channel} onValueChange={(v) => update("channel", v)}>
                  <SelectTrigger data-testid="campaign-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">WhatsApp + E-mail</SelectItem>
                    <SelectItem value="whatsapp">Somente WhatsApp</SelectItem>
                    <SelectItem value="email">Somente E-mail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(form.channel === "email" || form.channel === "both") && (
                <div><Label>Assunto (e-mail)</Label><Input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="Olá {{nome}}, ideia para {{empresa}}" data-testid="campaign-subject" /></div>
              )}
              <div>
                <Label>Corpo da mensagem</Label>
                <Textarea rows={10} value={form.body} onChange={(e) => update("body", e.target.value)} placeholder="Olá {{nome}}, tudo bem? Vi que a {{empresa}}..." data-testid="campaign-body" />
                <p className="text-xs text-muted-foreground mt-2">Variáveis: <code className="text-[#C07C88]">{"{{nome}} {{nome_completo}} {{empresa}} {{cargo}} {{email}} {{remetente}}"}</code></p>
              </div>
            </div>
          </Card>

          {dispatch && (
            <Card className="p-6 shadow-none border-border">
              <div className="flex items-center justify-between mb-4">
                <p className="overline">Enviar ({dispatch.items.length} destinatários)</p>
                <Button size="sm" className="bg-[#10B981] hover:bg-emerald-600 text-white" onClick={markSent} data-testid="mark-sent-btn"><CheckCircle2 size={13} className="mr-1" /> Marcar como enviada</Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Clique em cada botão para abrir WhatsApp ou e-mail com a mensagem já preenchida. Contatos abertos ficam marcados.</p>
              <div className="divide-y divide-border max-h-[600px] overflow-auto">
                {dispatch.items.map((it) => (
                  <div key={it.contact_id} className="py-3 grid grid-cols-12 gap-2 items-center" data-testid={`dispatch-${it.contact_id}`}>
                    <div className="col-span-4 min-w-0">
                      <p className="font-medium text-sm truncate">{it.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{it.company}</p>
                    </div>
                    <div className="col-span-5 text-xs text-muted-foreground truncate">{it.body?.split("\n")[0]}…</div>
                    <div className="col-span-3 flex gap-1 justify-end">
                      {it.whatsapp_url && (form.channel !== "email") && (
                        <Button size="sm" variant={isSent(it, "whatsapp") ? "outline" : "default"} className={isSent(it, "whatsapp") ? "border-emerald-300 text-emerald-600" : "bg-[#25D366] hover:bg-[#1DA851] text-white"} onClick={() => openOne(it, "whatsapp")} data-testid={`send-wa-${it.contact_id}`}>
                          <MessageCircle size={13} className="mr-1" /> {isSent(it, "whatsapp") ? "Enviado" : "WhatsApp"}
                        </Button>
                      )}
                      {it.mailto_url && (form.channel !== "whatsapp") && (
                        <Button size="sm" variant={isSent(it, "email") ? "outline" : "default"} className={isSent(it, "email") ? "border-emerald-300 text-emerald-600" : "bg-[#3B82F6] hover:bg-blue-600 text-white"} onClick={() => openOne(it, "email")} data-testid={`send-mail-${it.contact_id}`}>
                          <Mail size={13} className="mr-1" /> {isSent(it, "email") ? "Enviado" : "E-mail"}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(it.body); toast.success("Copiado"); }}><Copy size={12} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-6 shadow-none border-border">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Destinatários ({form.recipient_ids.length})</p>
              <div className="flex gap-1 text-[10px]">
                <button className="hover:underline" onClick={selectAll}>Todos</button>
                <span>·</span>
                <button className="hover:underline" onClick={clearAll}>Nenhum</button>
              </div>
            </div>
            <div className="max-h-64 overflow-auto space-y-1 border border-border rounded-md p-2">
              {contacts.map((c) => (
                <label key={c.contact_id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-[#FAFAFA] rounded px-1">
                  <Checkbox checked={form.recipient_ids.includes(c.contact_id)} onCheckedChange={() => toggleRecipient(c.contact_id)} data-testid={`select-recipient-${c.contact_id}`} />
                  <span className="flex-1 min-w-0">
                    <span className="truncate block">{c.name}</span>
                    <span className="text-xs text-muted-foreground truncate block">{c.company}</span>
                  </span>
                </label>
              ))}
              {contacts.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Cadastre contatos primeiro</p>}
            </div>
            <Button className="w-full mt-3 bg-[#C07C88] hover:bg-[#A86571]" onClick={loadDispatch} disabled={form.recipient_ids.length === 0} data-testid="prepare-dispatch-btn">
              <Send size={14} className="mr-1" /> Preparar envio
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
